import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
    FaBolt,
    FaShieldAlt,
    FaArrowRight,
    FaCogs,
    FaIndustry,
    FaCheckCircle,
    FaMap,
    FaBuilding,
    FaHardHat,
    FaClock,
    FaEye,
    FaPlus,
    FaStar
} from 'react-icons/fa';

const ServiceCategories = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const navigate = useNavigate();

    // Navigation handler functions
    const handleGetQuote = () => {
        navigate('/contact');
    };

    const handleViewAllServices = () => {
        navigate('/service');
    };

    const handleServiceDetails = (serviceType) => {
        navigate('/service', { state: { activeService: serviceType } });
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                duration: 0.6
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    // Only ONE featured service
    const featuredService = {
        id: 1,
        title: "Electrical Installation",
        subtitle: "Professional electrical material installation services",
        description: "Our electrical installation service covers the physical process of installing electrical materials at specified locations, following approved electrical drawings, safety standards, and industry best practices.",
        icon: FaBolt,
        image: "/images/electrical_installation.webp",
        serviceType: "electrical-installation",
        features: [
            "Installation per Approved Drawings",
            "Safety Standards Compliance",
            "Quality Control & Testing",
            "Commissioning & Handover"
        ]
    };

    return (
        <section className="py-16 md:py-24 relative overflow-hidden min-h-screen">
            {/* Background Image */}
            <div
                className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-fixed"
                style={{
                    backgroundImage: `url('/images/service_background.webp')`
                }}
            />

            {/* Static Overlay */}
            <div className="absolute inset-0 z-10 bg-gradient-to-br from-gray-900/85 via-gray-800/90 to-gray-900/85" />

            {/* Animated Background Elements */}
            <div className="absolute inset-0 z-20 opacity-10">
                <motion.div
                    className="absolute top-20 left-10 w-96 h-96 bg-secondary rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <motion.div
                    className="absolute bottom-20 right-20 w-72 h-72 bg-blue-400 rounded-full blur-3xl"
                    animate={{
                        scale: [1.2, 1, 1.2],
                        opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 2
                    }}
                />
            </div>

            <div className="container mx-auto px-4 md:px-8 relative z-30">
                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-12 md:mb-16"
                >
                    <motion.div
                        variants={itemVariants}
                        className="inline-flex items-center gap-2 bg-secondary/90 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-lg border border-secondary/20"
                    >
                        <FaIndustry className="text-primary text-sm" />
                        <span className="text-primary font-bold text-sm uppercase tracking-wider">
                            PROFESSIONAL SERVICES
                        </span>
                    </motion.div>

                    <motion.h2
                        variants={itemVariants}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-2xl"
                    >
                        Quick and Fastest
                        <span className="block text-secondary drop-shadow-lg">Industrial Solutions</span>
                    </motion.h2>

                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed drop-shadow-lg mb-8"
                    >
                        From electrical installations to comprehensive building maintenance, we provide
                        expert services that ensure safety, efficiency, and reliability for your projects.
                    </motion.p>

                    {/* Service Badge */}
                    <motion.div
                        variants={itemVariants}
                        className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20"
                    >
                        <FaStar className="text-secondary text-sm" />
                        <span className="text-gray-200 font-medium text-sm">
                            Featured Service Highlight
                        </span>
                    </motion.div>
                </motion.div>

                {/* Single Featured Service Content */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 md:p-12 border border-white/20 shadow-2xl mb-16"
                >
                    <motion.div
                        variants={itemVariants}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center"
                    >
                        {/* Content Side */}
                        <div className="order-2 lg:order-1">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-secondary/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-secondary/30">
                                    <FaBolt className="text-2xl text-secondary" />
                                </div>
                                <div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg">
                                        {featuredService.title}
                                    </h3>
                                    <p className="text-secondary font-semibold drop-shadow-sm">
                                        {featuredService.subtitle}
                                    </p>
                                </div>
                            </div>

                            <p className="text-gray-200 text-lg leading-relaxed mb-8 drop-shadow-sm">
                                {featuredService.description}
                            </p>

                            {/* Features List */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                                {featuredService.features.map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10"
                                    >
                                        <FaCheckCircle className="text-secondary text-sm flex-shrink-0" />
                                        <span className="text-gray-200 text-sm font-medium">{feature}</span>
                                    </motion.div>
                                ))}
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={handleGetQuote}
                                    className="bg-secondary/90 backdrop-blur-sm text-primary px-8 py-4 rounded-xl font-bold text-lg hover:bg-secondary transition-all duration-300 flex items-center justify-center gap-3 shadow-xl border border-secondary/30 cursor-pointer"
                                >
                                    <FaHardHat />
                                    Get Quote
                                    <FaArrowRight />
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleServiceDetails(featuredService.serviceType)}
                                    className="border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer"
                                >
                                    View More
                                </motion.button>
                            </div>
                        </div>

                        {/* Image Side */}
                        <div className="order-1 lg:order-2">
                            <motion.div
                                className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                                onClick={() => handleServiceDetails(featuredService.serviceType)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <img
                                    src={featuredService.image}
                                    alt={featuredService.title}
                                    className="w-full h-64 md:h-96 object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                                {/* Floating Badge */}
                                <div className="absolute top-6 left-6">
                                    <div className="bg-secondary/90 backdrop-blur-sm text-primary px-4 py-2 rounded-full text-sm font-bold border border-secondary/30 flex items-center gap-2">
                                        <FaStar />
                                        Featured Service
                                    </div>
                                </div>

                                {/* Service Number */}
                                <div className="absolute bottom-6 right-6">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30">
                                        <span className="text-white font-bold text-lg">01</span>
                                    </div>
                                </div>

                                {/* Hover Overlay */}
                                <div className="absolute inset-0 bg-primary/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                                        <FaArrowRight className="text-white text-2xl" />
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Large View All Services CTA */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 backdrop-blur-sm rounded-3xl p-8 md:p-16 border border-white/20 shadow-2xl"
                    >
                        <div className="max-w-4xl mx-auto">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="inline-block mb-8"
                            >
                                <FaEye className="text-5xl md:text-6xl text-secondary" />
                            </motion.div>

                            <h3 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
                                Discover All Our
                                <span className="block text-secondary">Professional Services</span>
                            </h3>

                            <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed max-w-3xl mx-auto">
                                Explore our complete range of industrial solutions including electrical mapping,
                                waterproofing, building maintenance, routine services, and specialized engineering solutions.
                            </p>

                            {/* Service Categories Preview */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
                                {[
                                    { icon: FaBolt, name: "Electrical" },
                                    { icon: FaShieldAlt, name: "Waterproofing" },
                                    { icon: FaCogs, name: "Maintenance" }
                                ].map((service, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 text-center"
                                    >
                                        <service.icon className="text-2xl text-secondary mx-auto mb-2" />
                                        <p className="text-gray-200 text-sm font-medium">{service.name}</p>
                                    </motion.div>
                                ))}
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleViewAllServices}
                                className="bg-gradient-to-r from-secondary via-yellow-400 to-secondary text-primary px-12 py-6 rounded-2xl font-black text-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 inline-flex items-center gap-4 border-2 border-secondary/30"
                            >
                                <FaIndustry className="text-xl" />
                                Explore All Services
                                <motion.div
                                    animate={{ x: [0, 8, 0] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                >
                                    <FaArrowRight className="text-xl" />
                                </motion.div>
                            </motion.button>

                            <div className="mt-8 text-gray-300">
                                <p className="text-lg font-semibold">5+ Professional Services • Expert Team • Quality Assured</p>
                                <p className="text-sm mt-2">Complete industrial solutions for all your business needs</p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Stats Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
                >
                    {[
                        { number: "20+", label: "Years Experience", action: () => navigate('/about') },
                        { number: "5+", label: "Service Categories", action: () => navigate('/service') },
                        { number: "100%", label: "Quality Assured", action: () => navigate('/about') },
                        { number: "500+", label: "Projects Completed", action: () => navigate('/service') }
                    ].map((stat, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg cursor-pointer hover:bg-white/15 transition-colors duration-300"
                            onClick={stat.action}
                            whileHover={{ scale: 1.05, y: -5 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <div className="text-2xl md:text-3xl font-bold text-secondary mb-2 drop-shadow-lg">
                                {stat.number}
                            </div>
                            <div className="text-gray-200 font-medium drop-shadow-sm">{stat.label}</div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Working Hours Info */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={itemVariants}
                    className="mt-12 bg-secondary/10 backdrop-blur-sm rounded-2xl p-6 border border-secondary/20 text-center"
                >
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <FaClock className="text-secondary text-2xl" />
                        <h3 className="text-xl font-bold text-white">Business Hours</h3>
                    </div>
                    <div className="text-gray-200">
                        <p className="mb-2"><strong className="text-secondary">Saturday - Thursday:</strong> 8:00 AM - 1:00 PM, 4:00 PM - 8:30 PM</p>
                        <p><strong className="text-secondary">Friday:</strong> Closed</p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ServiceCategories;